<div class="py-20 bg-light">
  <div class="container mx-auto px-6 max-w-md">
    <div class="bg-white dark:bg-slate-800 p-8 rounded-xl shadow-lg dark:shadow-slate-900/50 border">
      <!-- En-tête -->
      <div class="text-center mb-6">
        <h2 class="text-3xl font-bold text-secondary">
          Créer un nouveau mot de passe
        </h2>
        <p class="text-center text-gray-500 dark:text-gray-400 mt-2">
          Entrez votre nouveau mot de passe pour réinitialiser votre compte.
        </p>
      </div>

      <!-- Spinner de validation du token -->
      @if (isValidating) {
        <div class="flex justify-center items-center py-12">
          <svg
            class="animate-spin h-12 w-12 text-primary"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p class="ml-3 text-gray-600 dark:text-gray-400">Validation en cours...</p>
        </div>
      }

      <!-- Message d'erreur de validation -->
      @if (!isValidating && errorMessage && !isTokenValid) {
        <div
          class="bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500 text-red-700 dark:text-red-300 p-4 mb-6"
          role="alert"
          >
          <p class="font-semibold mb-2">❌ Erreur de lien</p>
          <p>{{ errorMessage }}</p>
          <button
            (click)="goToForgotPassword()"
            class="mt-4 w-full bg-red-500 text-white font-semibold py-2 rounded-lg hover:bg-red-600 transition-colors"
            >
            Demander un nouveau lien
          </button>
        </div>
      }

      <!-- Message de succès -->
      @if (successMessage) {
        <div
          class="bg-green-100 dark:bg-green-900/30 border-l-4 border-green-500 text-green-700 dark:text-green-300 p-4 mb-6"
          role="alert"
          >
          <p class="font-semibold mb-2">✅ Succès !</p>
          <p>{{ successMessage }}</p>
        </div>
      }

      <!-- Formulaire (visible si token valide) -->
      @if (!isValidating && isTokenValid && !successMessage) {
        <form
          [formGroup]="resetPasswordForm"
          (ngSubmit)="onSubmit()"
          >
          <!-- Nouveau mot de passe -->
          <div class="mb-4">
            <label for="password" class="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
              >Nouveau mot de passe</label
              >
              <input
                type="password"
                id="password"
                formControlName="password"
                class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                placeholder="••••••••"
                [disabled]="isLoading"
                />
              <!-- Erreurs de validation password -->
              @if (
                resetPasswordForm.get('password')?.invalid &&
                resetPasswordForm.get('password')?.touched
                ) {
                <p class="text-red-500 text-sm mt-1">
                  @if (resetPasswordForm.get('password')?.errors?.['required']) {
                    <span
                      >Le mot de passe est requis.</span
                      >
                    }
                    @if (resetPasswordForm.get('password')?.errors?.['minlength']) {
                      <span
                        >Le mot de passe doit contenir au moins 8 caractères.</span
                        >
                      }
                    </p>
                  }
                </div>
                <!-- Confirmer mot de passe -->
                <div class="mb-6">
                  <label
                    for="confirmPassword"
                    class="block text-gray-700 dark:text-gray-300 font-semibold mb-2"
                    >Confirmer le mot de passe</label
                    >
                    <input
                      type="password"
                      id="confirmPassword"
                      formControlName="confirmPassword"
                      class="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                      placeholder="••••••••"
                      [disabled]="isLoading"
                      />
                    <!-- Erreurs de validation confirmPassword -->
                    @if (
                      resetPasswordForm.get('confirmPassword')?.invalid &&
                      resetPasswordForm.get('confirmPassword')?.touched
                      ) {
                      <p class="text-red-500 text-sm mt-1">
                        @if (resetPasswordForm.get('confirmPassword')?.errors?.['required']) {
                          <span
                            >Veuillez confirmer votre mot de passe.</span
                            >
                          }
                        </p>
                      }
                    </div>
                    <!-- Avertissement: Mots de passe non identiques -->
                    @if (resetPasswordForm.get('password')?.value && resetPasswordForm.get('confirmPassword')?.value && resetPasswordForm.errors?.['passwordMismatch']) {
                      <div
                        class="bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500 text-yellow-700 dark:text-yellow-300 p-3 mb-6"
                        role="alert"
                        >
                        <p class="text-sm">⚠️ Les deux mots de passe ne correspondent pas.</p>
                      </div>
                    }
                    <!-- Avertissement: Force du mot de passe -->
                    @if (showPasswordStrengthError) {
                      <div
                        class="bg-yellow-100 dark:bg-yellow-900/30 border-l-4 border-yellow-500 text-yellow-700 dark:text-yellow-300 p-3 mb-6"
                        role="alert"
                        >
                        <p class="text-sm font-semibold mb-2">⚠️ Mot de passe faible</p>
                        <p class="text-sm">{{ getPasswordStrengthMessage() }}</p>
                      </div>
                    }
                    <!-- Exigences du mot de passe -->
                    <div
                      class="bg-blue-50 dark:bg-blue-900/20 border-l-4 border-blue-400 dark:border-blue-700 text-blue-700 dark:text-blue-300 p-3 mb-6"
                      role="info"
                      >
                      <p class="text-sm font-semibold mb-2">
                        ℹ️ Exigences du mot de passe:
                      </p>
                      <ul class="text-sm list-disc list-inside space-y-1">
                        <li>Minimum 8 caractères</li>
                        <li>Au moins une majuscule (A-Z)</li>
                        <li>Au moins une minuscule (a-z)</li>
                        <li>Au moins un chiffre (0-9)</li>
                        <li>Au moins un caractère spécial (&#64;$!%*?&)</li>
                      </ul>
                    </div>
                    <!-- Bouton de soumission -->
                    <button
                      type="submit"
          [disabled]="
            resetPasswordForm.invalid ||
            isLoading ||
            (resetPasswordForm.get('password')?.value &&
              !isPasswordStrong(resetPasswordForm.get('password')?.value))
          "
                      class="w-full bg-purple-600 text-white font-semibold text-lg py-3 rounded-lg hover:bg-purple-700 transition-colors shadow-md dark:shadow-slate-900/50 disabled:bg-purple-300 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                      @if (isLoading) {
                        <svg
                          class="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          ></circle>
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        <span>Réinitialisation en cours...</span>
                      } @else {
                        <span>Réinitialiser mon mot de passe</span>
                      }
                    </button>
                  </form>
                }

                <!-- Lien retour (avant succès) -->
                @if (!isValidating && isTokenValid && !successMessage) {
                  <p
                    class="text-center text-gray-600 dark:text-gray-400 mt-6"
                    >
                    <a
                      routerLink="/login"
                      class="text-primary hover:underline font-semibold"
                      >Retour à la connexion</a
                      >
                    </p>
                  }
                </div>
              </div>
            </div>
